import { MoneyValueDto } from '@/types/MoneyValueDto.ts';
import { PaymentMethod } from '@/features/dashboard/tourConfig/types/PaymentMethod.ts';
import { getPlannedTransactionKindLabel } from '@/features/dashboard/plannedTransactions/types/PlannedTransactionKind.utils.ts';
import {
  getScheduleFreezeReasonLabel,
  scheduleFreezeReasonHints,
} from '@/features/dashboard/plannedTransactions/types/ScheduleFreezeReason.utils.ts';
import { TourConsentDto } from '@/features/dashboard/tourConsents/types/TourConsentDto.ts';
import {
  ChangeSchedulePreviewDto,
  ScheduleCorrectionLineDto,
  SchedulePreviewLineDto,
} from '@/features/dashboard/bookingChanges/types/dto/ChangeSchedulePreviewDto.ts';
import {
  ChangePackageSettlementVm,
  CorrectionTermsVm,
  SettlementScheduleRowVm,
  SettlementScheduleVm,
} from '@/features/dashboard/bookingChanges/types/ChangePackageFlowViewModel.ts';
import { CHANGE_ROUTE_OPTIONS } from '@/features/dashboard/bookingChanges/types/ChangeRoute.utils.ts';
import { settlementDistributionOptions } from '@/features/dashboard/bookingChanges/types/SettlementDistribution.utils.ts';
import { tourConsentDocumentsMapper } from '@/features/dashboard/bookingChanges/types/TourConsentDto.mapper.ts';
import { formatDate } from '@/features/dashboard/bookingChanges/utils/formatters.ts';

/**
 * Mapper kroku W2: harmonogram po zmianie, warunki korekty i dokumenty.
 *
 * Sumy harmonogramu i kwota paczki jadą listami po jednej pozycji na walutę
 * i takie zostają — rezerwacja bywa wielowalutowa, a raty potrafią być
 * wystawione w dwóch walutach naraz. Sama rata ma jedną, bo tyle niesie
 * `PlannedTransactionDetailDto`.
 */

/** Przelew jest zawsze dopuszczalny, więc to on zostaje, gdy nie ma czego odziedziczyć. */
const FALLBACK_METHOD = PaymentMethod.TRANSFER;

/** Rata bierze udział w rozkładzie, dopóki nie ma powodu zamrożenia. */
const isAdjustable = (line: SchedulePreviewLineDto): boolean =>
  line.frozenReason === null;

const toScheduleRow = (
  line: SchedulePreviewLineDto,
): SettlementScheduleRowVm => {
  const changed =
    line.cancelled || line.amountAfter.amount !== line.current.amount.amount;

  return {
    id: line.current.id,
    name: line.current.description ?? '',
    dueDateLabel: formatDate(line.current.dueDate),
    amount: [line.amountAfter],
    previousAmount: changed ? [line.current.amount] : undefined,
    unchanged: !changed || undefined,
    cancelled: line.cancelled || undefined,
    frozenLabel: line.frozenReason
      ? getScheduleFreezeReasonLabel(line.frozenReason)
      : undefined,
    frozenHint: line.frozenReason
      ? scheduleFreezeReasonHints[line.frozenReason]
      : undefined,
  };
};

/**
 * Linia resztkowa jako wiersz harmonogramu. Termin bywa pusty — paczka nosi go
 * dopiero od chwili, gdy operator go poda, więc do tego czasu wiersz mówi, że
 * termin dopiero powstanie.
 */
const toCorrectionRow = (
  correction: ScheduleCorrectionLineDto,
  index: number,
): SettlementScheduleRowVm => ({
  id: `correction-${index}`,
  name: getPlannedTransactionKindLabel(correction.kind),
  dueDateLabel: correction.dueDate
    ? formatDate(correction.dueDate)
    : 'termin do ustalenia',
  amount: [correction.amount],
  isNew: true,
});

/**
 * Domyślny termin korekty: ostatnia rata biorąca udział w rozkładzie. Bez rat
 * nie ma czego zaproponować — operator podaje datę sam.
 */
const defaultDueDate = (dto: ChangeSchedulePreviewDto): string | null => {
  const carried = dto.corrections.find(
    (correction) => correction.dueDate !== null,
  )?.dueDate;
  if (carried) {
    return carried;
  }

  const adjustable = dto.lines.filter(isAdjustable);
  return adjustable[adjustable.length - 1]?.current.dueDate ?? null;
};

/**
 * Metody płatności korekty dziedziczone po ratach: część wspólna metod
 * dopuszczonych przez raty biorące udział w rozkładzie. Pusta część wspólna
 * (albo brak takich rat) zostawia przelew.
 */
const inheritedMethods = (dto: ChangeSchedulePreviewDto): PaymentMethod[] => {
  const carried = dto.corrections.find(
    (correction) => correction.allowedPaymentMethods.length > 0,
  )?.allowedPaymentMethods;
  if (carried) {
    return carried;
  }

  const allowed = dto.lines
    .filter(isAdjustable)
    .map((line) => line.current.allowedPaymentMethods);
  if (allowed.length === 0) {
    return [FALLBACK_METHOD];
  }

  const shared = allowed.reduce((common, methods) =>
    common.filter((method) => methods.includes(method)),
  );
  return shared.length > 0 ? shared : [FALLBACK_METHOD];
};

/** Metody, spośród których operator wybiera — suma dopuszczeń rat w rozkładzie. */
const methodChoices = (dto: ChangeSchedulePreviewDto): PaymentMethod[] => {
  const union = [
    ...new Set(
      dto.lines
        .filter(isAdjustable)
        .flatMap((line) => line.current.allowedPaymentMethods),
    ),
  ];
  return union.length > 0 ? union : [FALLBACK_METHOD];
};

export const changeSchedulePreviewMapper = {
  /** Harmonogram po zmianie: raty w kolejności terminów, korekty na końcu. */
  toSchedule(dto: ChangeSchedulePreviewDto): SettlementScheduleVm {
    return {
      rows: [
        ...dto.lines.map(toScheduleRow),
        ...dto.corrections.map(toCorrectionRow),
      ],
      totalBefore: dto.totalBefore,
      totalAfter: dto.totalAfter,
    };
  },

  /** Propozycja terminu i metod płatności linii korekty. */
  toCorrectionTerms(dto: ChangeSchedulePreviewDto): CorrectionTermsVm {
    return {
      dueDate: defaultDueDate(dto),
      paymentMethods: inheritedMethods(dto),
      methodChoices: methodChoices(dto),
    };
  },

  /** Pełne wejście kroku W2. */
  toSettlementVm(
    dto: ChangeSchedulePreviewDto,
    consents: TourConsentDto[],
    paid: MoneyValueDto[],
    packageNumber: number,
  ): ChangePackageSettlementVm {
    const schedule = this.toSchedule(dto);

    return {
      packageNumber,
      balance: dto.delta,
      paid,
      dueBefore: schedule.totalBefore,
      adjustableCount: dto.lines.filter(isAdjustable).length,
      options: settlementDistributionOptions(dto.delta),
      schedule,
      correctionTerms: this.toCorrectionTerms(dto),
      routes: CHANGE_ROUTE_OPTIONS,
      documents: tourConsentDocumentsMapper.toDocuments(consents),
    };
  },
};
